/**
 * Adaptador de autenticación sobre Firebase Auth + RTDB.
 *
 * Gate de rol FAIL-CLOSED: un usuario logado solo es admin si existe el nodo
 * `/admin/{uid}` en RTDB (paridad con el LoginScreen legacy de admin_app, que
 * cierra por rol). Ante un error de lectura no se muestra contenido y la UI
 * ofrece un reintento, sin confundir un fallo de red con una denegación real.
 *
 * NOTA F0: en F4 (`useNestAuth`) el rol pasará a resolverse server-side vía
 * NestAuth (rider-core `NestAuthSession`) en lugar de leer RTDB directo.
 */
import {
  GoogleAuthProvider, OAuthProvider, signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, sendPasswordResetEmail,
  type User,
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { get, ref } from 'firebase/database';
import { getFirebase } from '../firebase/firebase';
import type { AuthSource, AuthState } from './types';
import { adminPushLifecycle } from '../notifications/webPushLifecycle';

async function resolveIsAdmin(uid: string): Promise<boolean> {
  const { db } = getFirebase();
  const snap = await get(ref(db, `admin/${uid}`));
  return snap.exists();
}

export function createFirebaseAuthSource(): AuthSource {
  const { auth } = getFirebase();
  const refreshers = new Set<() => Promise<void>>();

  return {
    subscribe(onChange: (state: AuthState) => void): () => void {
      let active = true;
      let roleResolution = 0;
      let previousUid: string | undefined;
      const push = adminPushLifecycle(async (owner, force) => {
        const { restoreAdminWebPush } = await import('../notifications/webPush');
        await restoreAdminWebPush(owner, force);
      });
      const visibility = () => { if (document.visibilityState === 'visible') push.resume(); };
      document.addEventListener('visibilitychange', visibility);
      let stopTokenRefresh: (() => void) | undefined;
      void import('@ibercar/rider-core/firebase').then(({ onWebPushTokenRefresh }) => {
        if (active) stopTokenRefresh = onWebPushTokenRefresh(() => push.resume(true));
      }).catch(() => undefined);
      const resolve = async (user: User | null) => {
        if (!active) return;
        const currentResolution = ++roleResolution;
        const retired = previousUid; previousUid = user?.uid;
        if (retired && retired !== user?.uid) {
          push.update(undefined, false);
          void import('../notifications/webPush').then(({ logoutAdminWebPush }) => logoutAdminWebPush(retired)).catch(() => undefined);
        }
        if (user === null) {
          push.update(undefined, false);
          onChange({ status: 'signed-out', user: null, isAdmin: false });
          return;
        }
        const adminUser = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
        };

        // El rol sigue sin estar resuelto: no es una denegación.
        onChange({
          status: 'checking-role',
          user: adminUser,
          isAdmin: false,
        });
        await resolveIsAdmin(user.uid).then(
          (isAdmin) => {
            // Ignora resoluciones de una sesión anterior o posteriores al cleanup.
            if (!active || currentResolution !== roleResolution) return;
            push.update(user.uid, isAdmin);
            onChange({
              status: 'signed-in',
              user: adminUser,
              isAdmin,
            });
          },
          () => {
            // Un fallo de red termina en denegación fail-closed, no en un
            // spinner infinito ni en una sesión con privilegios dudosos.
            if (!active || currentResolution !== roleResolution) return;
            onChange({
              status: 'role-error',
              user: adminUser,
              isAdmin: false,
            });
          },
        );
      };
      const refresh = () => resolve(auth.currentUser);
      refreshers.add(refresh);
      const unsubscribe = onAuthStateChanged(auth, user => { void resolve(user); });
      return () => {
        refreshers.delete(refresh);
        active = false;
        push.stop(); stopTokenRefresh?.(); document.removeEventListener('visibilitychange', visibility);
        roleResolution++;
        unsubscribe();
      };
    },
    async signInWithGoogle(): Promise<void> {
      await signInWithPopup(auth, new GoogleAuthProvider());
    },
    async signInWithEmail(email, password) { await signInWithEmailAndPassword(auth, email.trim(), password); },
    async signInWithApple() { const provider = new OAuthProvider('apple.com'); provider.addScope('email'); provider.addScope('name'); await signInWithPopup(auth, provider); },
    async resetPassword(email) { await sendPasswordResetEmail(auth, email.trim()); },
    async register(name, email, password) {
      if (auth.currentUser) throw new Error('Ya hay una sesión iniciada. Continúa con esta cuenta o ciérrala antes de crear otra.');
      const credential = await createUserWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
      if ((auth.currentUser as User | null)?.uid !== credential.user.uid) throw new Error('La cuenta ha cambiado.');
      await updateProfile(credential.user, { displayName: name.trim() });
      if ((auth.currentUser as User | null)?.uid !== credential.user.uid) throw new Error('La cuenta ha cambiado.');
    },
    async refresh() { await auth.currentUser?.reload(); await auth.currentUser?.getIdToken(true); await Promise.all([...refreshers].map(refresh => refresh())); },
    async signOut(): Promise<void> {
      const owner = auth.currentUser?.uid;
      if (owner) {
        try {
          const { logoutAdminWebPush } = await import('../notifications/webPush');
          await logoutAdminWebPush(owner);
        } catch { /* La baja remota no impide cerrar la cuenta. */ }
      }
      if (auth.currentUser?.uid === owner) await firebaseSignOut(auth);
    },
  };
}
